/**
 * Integrations access — which Integrations sections a viewer may see.
 *
 * Single source for the sidebar item (useMenuConfig), the Integrations page, and the
 * project default redirect (sidebar-by-subscription plan 2026-10-07, D-9 / D-11 / D-21).
 * Gates are UI-only: the section endpoints keep their current server behaviour.
 *
 * Pure module: no React, no I/O.
 */

/** Accordion keys on the Integrations page, in page order. */
export type IntegrationSectionKey =
  | 'domain'
  | 'stripe'
  | 'paddle'
  | 'analytics'
  | 'meta-ads'
  | 'google-ads'
  | 'tiktok-ads'
  | 'x-twitter'
  | 'reddit'
  | 'ai-keys'
  | 'locales-content-plan';

const WEB_ONBOARDING = 'web_onboarding.access';
const CONTENT_PLAN = 'content_plan.access';
const PRODUCT_WORKSPACE = 'product_analysis.access';
const ARTICLES = 'articles.access';

/** A section opens when the viewer holds ANY of its capabilities. */
const SECTION_CAPABILITIES: Readonly<Record<IntegrationSectionKey, readonly string[]>> = {
  domain: [WEB_ONBOARDING],
  stripe: [WEB_ONBOARDING],
  paddle: [WEB_ONBOARDING],
  analytics: [WEB_ONBOARDING, ARTICLES],
  'meta-ads': [WEB_ONBOARDING],
  'google-ads': [WEB_ONBOARDING],
  'tiktok-ads': [WEB_ONBOARDING],
  'x-twitter': [CONTENT_PLAN],
  reddit: [CONTENT_PLAN],
  'ai-keys': [CONTENT_PLAN, PRODUCT_WORKSPACE],
  'locales-content-plan': [CONTENT_PLAN],
};

export const INTEGRATION_SECTION_ORDER: readonly IntegrationSectionKey[] = [
  'domain',
  'stripe',
  'paddle',
  'analytics',
  'meta-ads',
  'google-ads',
  'tiktok-ads',
  'x-twitter',
  'reddit',
  'ai-keys',
  'locales-content-plan',
];

/** Every capability that opens at least one section (for callers that must preload them). */
export const INTEGRATION_ACCESS_CAPABILITIES: readonly string[] = [
  WEB_ONBOARDING,
  CONTENT_PLAN,
  PRODUCT_WORKSPACE,
  ARTICLES,
];

export interface IntegrationSectionAccess {
  /** Per-section visibility. */
  sections: Readonly<Record<IntegrationSectionKey, boolean>>;
  /** Visible sections in page order. */
  visibleSections: readonly IntegrationSectionKey[];
  /** True when the viewer sees at least one section (drives the sidebar item). */
  hasAny: boolean;
}

/**
 * Resolve section visibility. Admins see every section (UI admin bypass).
 *
 * @param hasCapability advisory capability resolver (billing access snapshot)
 * @param isAdmin platform admin
 */
export function getIntegrationSectionAccess(
  hasCapability: ((capability: string) => boolean) | undefined,
  isAdmin: boolean,
): IntegrationSectionAccess {
  const sections = {} as Record<IntegrationSectionKey, boolean>;
  for (const key of INTEGRATION_SECTION_ORDER) {
    sections[key] = isAdmin || SECTION_CAPABILITIES[key].some((capability) => Boolean(hasCapability?.(capability)));
  }
  const visibleSections = INTEGRATION_SECTION_ORDER.filter((key) => sections[key]);
  return { sections, visibleSections, hasAny: visibleSections.length > 0 };
}
